import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Form, Input, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ShoppingOutlined } from '@ant-design/icons';
import BASEURL from '../API/Config';

const UnitMaster = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/units`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUnits(Array.isArray(data) ? data : []);
    } catch {
      Swal.fire('Error', 'Failed to load units.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUnits(); }, []);

  const openModal = (unit = null) => {
    if (unit) {
      setEditId(unit._id);
      form.setFieldsValue({ title: unit.title });
    } else {
      setEditId(null);
      form.resetFields();
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    try {
      const url = editId ? `${BASEURL}/units/${editId}` : `${BASEURL}/units`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: values.title }),
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Success', `Unit ${editId ? 'updated' : 'added'}!`, 'success');
      setShowModal(false);
      fetchUnits();
    } catch (err) {
      Swal.fire('Error', err.message || 'Failed.', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Unit?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${BASEURL}/units/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Deleted!', 'Unit removed.', 'success');
      fetchUnits();
    } catch (err) {
      Swal.fire('Error', err.message || 'Delete failed.', 'error');
    }
  };

  const filtered = units.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));

  const columns = [
    {
      title: "S.No", key: "idx", width: 60,
      render: (_, __, i) => <span style={{ color: "#6b7280" }}>{i + 1}</span>,
    },
    {
      title: 'UNIT NAME', key: 'title',
      render: (r) => (
        <Space>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e53935' }}>
            <ShoppingOutlined />
          </div>
          <span style={{ fontWeight: 600, color: '#212121' }}>{r.title}</span>
        </Space>
      ),
    },
    {
      title: 'STATUS', key: 'status',
      render: () => <Tag color="green" style={{ borderRadius: 50, fontWeight: 600 }}>Active</Tag>,
    },
    {
      title: 'ACTIONS', key: 'actions', width: 110,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Catalog</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Unit Master</h2>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
          Add Unit
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input allowClear prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search unit..."
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260, borderRadius: 4 }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="_id" loading={loading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"], showTotal: (t) => `${t} units` }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{editId ? 'Edit Unit' : 'Add Unit'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={420}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Unit Title"
            rules={[{ required: true, message: 'Title is required' }]}>
            <Input prefix={<ShoppingOutlined />} placeholder="Enter unit name (e.g. Kg, 1/2 Kg)" size="large" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {editId ? 'Update' : 'Add Unit'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UnitMaster;

